import type { PersistentType, ReportNameType, ReportSubType, ReportType } from './reportConfigurationsTypes';

// ---------------------------------------------------------------------------
// PersistentType groupings — business rules for which actions are allowed
// ---------------------------------------------------------------------------

/** PersistentTypes that represent an active working copy (tab). */
export const OPEN_PERSISTENT_TYPES: ReadonlySet<PersistentType> = new Set(['OPEN']);

/** PersistentTypes that represent a persisted saved report. */
export const SAVED_PERSISTENT_TYPES: ReadonlySet<PersistentType> = new Set(['SAVED', 'CUSTOM']);

/** PersistentTypes that allow "Save as new report". */
export const SAVE_AS_NEW_PERSISTENT_TYPES: ReadonlySet<PersistentType> = new Set([
  'CUSTOM',
  'OPEN',
  'TEMPORARY',
  'SAVED',
]);

// ---------------------------------------------------------------------------
// Report tile metadata — shared between web and mobile
// ---------------------------------------------------------------------------

export type TileChip = 'Business' | 'Snapshot';

/**
 * Parsed representation of a saved-report title that may end with " (N)".
 * We keep the base name separate so numbering logic can compare report families.
 */
type ParsedReportName = {
  baseName: string;
  copyNumber?: number;
};

/**
 * Icon keys corresponding to custom SVG assets in core/assets/report-icons/.
 * Each platform maps these keys to its own icon component.
 */
export type ReportIconKey =
  | 'profit-loss'
  | 'balance-sheet'
  | 'spending'
  | 'income'
  | 'net-worth'
  | 'taxes'
  | 'savings'
  | 'monthly-summary';

export type ReportTileConfig = {
  /** Matches the predefined config `id` from the /defaults API */
  id: string;
  displayName: string;
  description: string;
  reportName: ReportNameType;
  reportSubType: ReportSubType;
  /** Key into core/assets/report-icons/ — each platform resolves to its own icon component */
  iconKey: ReportIconKey;
  /** Palette path for the icon foreground color, e.g. 'success.main' */
  iconColorPath: string;
  /** Palette path for the icon background color, e.g. 'success.light' */
  iconBgColorPath: string;
  chip?: TileChip;
  /** When true the tile is only visible when business entitlements are enabled */
  businessOnly?: boolean;
  /** When true this tile renders in a compact row style instead of as a card */
  rowStyle?: boolean;
};

export type ReportTileConfigLookup = {
  predefinedConfigId?: string;
  name?: string;
  reportType?: ReportType;
  reportSubType?: ReportSubType;
};

/**
 * Ordered list of report tiles shown on the Reports home page.
 * Shared across web and mobile for consistent ordering and metadata.
 *
 * `id` matches the predefined config `id` returned by the /defaults API so that
 * consumers can resolve tile metadata directly from `predefinedConfigId`.
 */
export const REPORT_TILE_CONFIGS: ReportTileConfig[] = [
  {
    id: '8',
    displayName: 'Profit & Loss',
    description: 'Business inflows & outflows',
    reportName: 'profit-loss',
    reportSubType: 'NET_INCOME',
    iconKey: 'profit-loss',
    iconColorPath: 'data.sequenceDark.2',
    iconBgColorPath: 'data.sequenceLight.2',
    chip: 'Business',
    businessOnly: true,
  },
  {
    id: '9',
    displayName: 'Balance Sheet',
    description: 'Assets, Liabilities & Equity',
    reportName: 'balance-sheet',
    reportSubType: 'BALANCE_SHEET',
    iconKey: 'balance-sheet',
    iconColorPath: 'data.sequenceDark.8',
    iconBgColorPath: 'data.sequenceLight.8',
    chip: 'Business',
    businessOnly: true,
  },
  {
    id: '1',
    displayName: 'Spending',
    description: 'See where your money goes',
    reportName: 'total-spending',
    reportSubType: 'SPENDING',
    iconKey: 'spending',
    iconColorPath: 'data.sequenceDark.0',
    iconBgColorPath: 'data.sequenceLight.0',
  },
  {
    id: '12',
    displayName: 'Uncategorized Spending',
    description: 'See where your money goes',
    reportName: 'Uncategorized Spending',
    reportSubType: 'SPENDING',
    iconKey: 'spending',
    iconColorPath: 'data.sequenceDark.0',
    iconBgColorPath: 'data.sequenceLight.0',
  },
  {
    id: '11',
    displayName: 'Spending Summary',
    description: 'View your spending over time',
    reportName: 'Spending Summary',
    reportSubType: 'SPENDING',
    iconKey: 'spending',
    iconColorPath: 'data.sequenceDark.0',
    iconBgColorPath: 'data.sequenceLight.0',
  },
  {
    id: '13',
    displayName: 'Last 3 Months of Spending',
    description: 'View your spending over time',
    reportName: 'Last 3 Months of Spending',
    reportSubType: 'SPENDING',
    iconKey: 'spending',
    iconColorPath: 'data.sequenceDark.0',
    iconBgColorPath: 'data.sequenceLight.0',
  },
  {
    id: '2',
    displayName: 'Income',
    description: 'Track your sources of income',
    reportName: 'income',
    reportSubType: 'INCOME',
    iconKey: 'income',
    iconColorPath: 'data.sequenceDark.2',
    iconBgColorPath: 'data.sequenceLight.2',
  },
  {
    id: '10',
    displayName: 'Income Summary',
    description: 'View your income over time',
    reportName: 'Income Summary',
    reportSubType: 'INCOME',
    iconKey: 'income',
    iconColorPath: 'data.sequenceDark.2',
    iconBgColorPath: 'data.sequenceLight.2',
  },
  {
    id: '3',
    displayName: 'Income & Expense',
    description: 'Compare income and expenses',
    reportName: 'income-expense',
    reportSubType: 'NET_INCOME',
    iconKey: 'profit-loss',
    iconColorPath: 'data.sequenceDark.2',
    iconBgColorPath: 'data.sequenceLight.2',
  },
  {
    id: '5',
    displayName: 'Net Worth',
    description: 'Your net worth over time',
    reportName: 'net-worth',
    reportSubType: 'NET_WORTH',
    iconKey: 'net-worth',
    iconColorPath: 'data.sequenceDark.7',
    iconBgColorPath: 'data.sequenceLight.7',
  },
  {
    id: '6',
    displayName: 'Taxes',
    description: 'Taxable income and deductions',
    reportName: 'taxes',
    reportSubType: 'TAX_SUMMARY',
    iconKey: 'taxes',
    iconColorPath: 'data.sequenceDark.1',
    iconBgColorPath: 'data.sequenceLight.1',
  },
  {
    id: '4',
    displayName: 'Savings',
    description: 'Savings progress over time',
    reportName: 'savings',
    reportSubType: 'SAVINGS',
    iconKey: 'savings',
    iconColorPath: 'data.sequenceDark.6',
    iconBgColorPath: 'data.sequenceLight.6',
  },
  {
    id: '0',
    displayName: 'Monthly Summary',
    description: 'A snapshot of your income, spending, and goals for the month.',
    reportName: 'monthly-summary',
    reportSubType: 'NONE',
    iconKey: 'monthly-summary',
    iconColorPath: 'data.sequenceDark.3',
    iconBgColorPath: 'data.sequenceLight.3',
    chip: 'Snapshot',
    rowStyle: true,
  },
];

const SUMMARY_TILE_CONFIG_ID_BY_SUBTYPE: Partial<Record<ReportSubType, string>> = {
  INCOME: '10',
  SPENDING: '11',
};

const TILE_CONFIG_ID_BY_SUBTYPE: Partial<Record<ReportSubType, string>> = {
  BALANCE_SHEET: '9',
  INCOME: '2',
  NET_INCOME: '3',
  NET_WORTH: '5',
  SAVINGS: '4',
  SPENDING: '1',
  TAX_SCHEDULE: '6',
  TAX_SUMMARY: '6',
};

/** Lookup tile config by predefined config id (matches `predefinedConfigId` on open/saved configs). */
export const TILE_CONFIG_BY_ID = new Map<string, ReportTileConfig>(REPORT_TILE_CONFIGS.map((t) => [t.id, t]));

/** Fallback lookup by `reportName` for legacy configs without a `predefinedConfigId`. */
const TILE_CONFIG_BY_REPORT_NAME = new Map<string, ReportTileConfig>(REPORT_TILE_CONFIGS.map((t) => [t.reportName, t]));

// Matches a trailing numeric copy suffix like "Profit & Loss (3)".
const NUMERIC_SUFFIX_REGEX = /\s+\((\d+)\)$/;

/**
 * Find the tile config for a report using its `predefinedConfigId`.
 * For PREDEFINED configs, pass `config.id`. For OPEN/SAVED configs, pass `config.predefinedConfigId`.
 */
export const findTileConfig = (predefinedConfigId?: string, reportName?: string): ReportTileConfig | undefined => {
  if (predefinedConfigId) {
    const byId = TILE_CONFIG_BY_ID.get(predefinedConfigId);
    if (byId) return byId;
  }
  return reportName ? TILE_CONFIG_BY_REPORT_NAME.get(reportName) : undefined;
};

/**
 * Find tile metadata using the report's current shape.
 * Income and Spending have separate transaction and summary tiles, so summary
 * report configs must resolve to the summary tile even if they still carry the
 * transaction tile's `predefinedConfigId`.
 */
export const findTileConfigForReportConfig = (config: ReportTileConfigLookup): ReportTileConfig | undefined => {
  if (config.reportType === 'SUMMARY') {
    const baseTileConfigId = config.reportSubType ? TILE_CONFIG_ID_BY_SUBTYPE[config.reportSubType] : undefined;

    if (config.predefinedConfigId && config.predefinedConfigId !== baseTileConfigId) {
      const exactTileConfig = findTileConfig(config.predefinedConfigId, config.name);
      if (exactTileConfig) {
        return exactTileConfig;
      }
    }

    const summaryTileConfigId = config.reportSubType
      ? SUMMARY_TILE_CONFIG_ID_BY_SUBTYPE[config.reportSubType]
      : undefined;
    const summaryTileConfig = summaryTileConfigId ? TILE_CONFIG_BY_ID.get(summaryTileConfigId) : undefined;
    if (summaryTileConfig) {
      return summaryTileConfig;
    }
  }

  const tileConfig = findTileConfig(config.predefinedConfigId, config.name);
  if (tileConfig) {
    return tileConfig;
  }

  const subtypeTileConfigId = config.reportSubType ? TILE_CONFIG_ID_BY_SUBTYPE[config.reportSubType] : undefined;
  return subtypeTileConfigId ? TILE_CONFIG_BY_ID.get(subtypeTileConfigId) : undefined;
};

/**
 * Resolve the display name for a report config.
 * If the user has entered a custom name (different from the predefined `reportName`), use it.
 * Otherwise, use the tile config's `displayName` (e.g. "Profit & Loss" instead of "profit-loss").
 * Falls back to `reportName` lookup for legacy configs without a `predefinedConfigId`.
 */
export const getReportDisplayName = (name: string, predefinedConfigId?: string): string => {
  const tileConfig = findTileConfig(predefinedConfigId) ?? TILE_CONFIG_BY_REPORT_NAME.get(name);
  if (tileConfig && name === tileConfig.reportName) {
    return tileConfig.displayName;
  }
  return name || tileConfig?.displayName || '';
};

export const getConfigDisplayName = (
  config: { description?: string; name?: string; predefinedConfigId?: string } | null | undefined,
): string => config?.description || (config?.name ? getReportDisplayName(config.name, config.predefinedConfigId) : '');

export const hasSavedReportParent = (
  config: { parentPersistentType?: PersistentType; isParentDeleted?: boolean } | null | undefined,
): boolean =>
  (config?.parentPersistentType === 'SAVED' || config?.parentPersistentType === 'CUSTOM') && !config?.isParentDeleted;

const parseReportName = (name: string): ParsedReportName => {
  const trimmedName = name.trim();
  const match = trimmedName.match(NUMERIC_SUFFIX_REGEX);

  if (!match) {
    return { baseName: trimmedName };
  }

  return {
    baseName: trimmedName.slice(0, match.index).trim(),
    copyNumber: Number(match[1]),
  };
};

export const getSavedReportBaseName = (name: string): string => parseReportName(name).baseName;

/**
 * Suggest the next saved-report name for a family of similarly named reports.
 * If the requested name already exists, the smallest missing positive suffix is
 * used so gaps are filled predictably.
 */
export const getSuggestedSavedReportName = (candidateName: string, existingNames: readonly string[]): string => {
  const trimmedCandidateName = candidateName.trim();
  if (!trimmedCandidateName) {
    return '';
  }

  const { baseName } = parseReportName(trimmedCandidateName);
  if (!baseName) {
    return trimmedCandidateName;
  }

  const normalizedBaseName = baseName.toLocaleLowerCase();
  const normalizedCandidateName = trimmedCandidateName.toLocaleLowerCase();
  const usedCopyNumbers = new Set<number>();
  let isCandidateNameTaken = false;

  existingNames.forEach((existingName) => {
    const trimmedExistingName = existingName.trim();
    if (!trimmedExistingName) {
      return;
    }

    if (trimmedExistingName.toLocaleLowerCase() === normalizedCandidateName) {
      isCandidateNameTaken = true;
    }

    const { baseName: existingBaseName, copyNumber } = parseReportName(trimmedExistingName);
    if (!existingBaseName || existingBaseName.toLocaleLowerCase() !== normalizedBaseName) {
      return;
    }

    if (copyNumber && copyNumber > 0) {
      usedCopyNumbers.add(copyNumber);
    }
  });

  if (!isCandidateNameTaken) {
    return trimmedCandidateName;
  }

  let nextCopyNumber = 1;
  while (usedCopyNumbers.has(nextCopyNumber)) {
    nextCopyNumber += 1;
  }

  return `${baseName} (${nextCopyNumber})`;
};
